import { useCallback, useEffect, useRef, useState } from "react"
import WaveSurfer from "wavesurfer.js"
import RecordPlugin, {
  type RecordPluginOptions,
} from "wavesurfer.js/dist/plugins/record.js"

import type { RobinVoiceTranscriptionResult } from "../api/voice-transcription-client"
import type { RobinVoiceTranscriptionErrorCode } from "../api/voice-transcription-errors"

const VOICE_RECORDING_MAX_BYTES = 10 * 1024 * 1024
const VOICE_RECORDING_MAX_MS = 60_000
const VOICE_RECORDING_TIMESLICE_MS = 250
const VOICE_RECORDING_STOP_TIMEOUT_MS = 3_000

export type DictationTranscriptionErrorCode = RobinVoiceTranscriptionErrorCode

export type DictationErrorCode =
  | "not-supported"
  | "not-allowed"
  | "audio-capture"
  | DictationTranscriptionErrorCode

export type DictationTranscriptionResult =
  RobinVoiceTranscriptionResult

type UseDictationOptions = {
  onTranscript: (transcript: string) => void
  transcribeAudio: (audioFile: File) => Promise<DictationTranscriptionResult>
}

export type Dictation = {
  /** Whether this browser can record microphone audio for transcription. */
  supported: boolean
  /** The live recording UI is active before the user stops the capture. */
  recording: boolean
  /** The microphone is actively producing audio samples. */
  listening: boolean
  /** A recorded blob is being transcribed into composer text. */
  transcribing: boolean
  /** The most recent user-recoverable voice error. */
  error: DictationErrorCode | null
  /** Begin a new voice recording session. */
  start: () => void
  /** Stop recording and transcribe the captured audio. */
  stop: () => void
  /** Hard-stop and discard the active recording or late transcription result. */
  abort: () => void
  /** Container callback where WaveSurfer renders the live waveform. */
  waveformContainerRef: (node: HTMLDivElement | null) => void
}

/**
 * Coordinates Robin composer voice input from live mic capture through final
 * transcription. The hook owns browser recording side effects and stale-session
 * suppression so the composer can treat speech like any other source of input
 * text: a final transcript is appended only after the user explicitly stops.
 */
export function useDictation({
  onTranscript,
  transcribeAudio,
}: UseDictationOptions): Dictation {
  const [supported] = useState(() => hasWaveSurferRecordingSupport())
  const [recording, setRecording] = useState(false)
  const [listening, setListening] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<DictationErrorCode | null>(null)
  const [waveformElement, setWaveformElement] =
    useState<HTMLDivElement | null>(null)

  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const recordRef = useRef<RecordPlugin | null>(null)
  const activeSessionIdRef = useRef<number | null>(null)
  const stoppingSessionIdRef = useRef<number | null>(null)
  const recordedBytesRef = useRef(0)
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unsubscriptionsRef = useRef<Array<() => void>>([])
  const generationRef = useRef(0)
  const mountedRef = useRef(true)
  const emitRef = useRef(onTranscript)
  const transcribeRef = useRef(transcribeAudio)

  emitRef.current = onTranscript
  transcribeRef.current = transcribeAudio

  const clearStopTimeout = useCallback(() => {
    if (stopTimeoutRef.current == null) return
    clearTimeout(stopTimeoutRef.current)
    stopTimeoutRef.current = null
  }, [])

  const destroyWaveSurfer = useCallback(() => {
    clearStopTimeout()
    unsubscriptionsRef.current.forEach((unsubscribe) => unsubscribe())
    unsubscriptionsRef.current = []
    recordRef.current = null
    const wavesurfer = wavesurferRef.current
    wavesurferRef.current = null
    wavesurfer?.destroy()
  }, [clearStopTimeout])

  const markInactive = useCallback(() => {
    clearStopTimeout()
    activeSessionIdRef.current = null
    stoppingSessionIdRef.current = null
    recordedBytesRef.current = 0
    setRecording(false)
    setListening(false)
  }, [clearStopTimeout])

  const finishRecording = useCallback(
    async (sessionId: number, audioBlob: Blob) => {
      if (activeSessionIdRef.current !== sessionId) {
        return
      }

      markInactive()
      destroyWaveSurfer()

      if (audioBlob.size <= 0) {
        setError("no-speech")
        setSubmitting(false)
        return
      }

      if (audioBlob.size > VOICE_RECORDING_MAX_BYTES) {
        setError("too-large")
        setSubmitting(false)
        return
      }

      setSubmitting(true)
      setError(null)

      const audioFile = new File(
        [audioBlob],
        voiceFileNameForMimeType(audioBlob.type),
        { type: audioBlob.type || "audio/webm" },
      )

      try {
        const result = await transcribeRef.current(audioFile).catch(() => ({
          ok: false as const,
          error: "transcription-failed" as const,
        }))
        if (
          !mountedRef.current ||
          generationRef.current !== sessionId
        ) {
          return
        }

        if (!result.ok) {
          setSubmitting(false)
          setError(result.error)
          return
        }

        const transcript = result.text.trim()
        if (!transcript) {
          setSubmitting(false)
          setError("no-speech")
          return
        }

        setSubmitting(false)
        emitRef.current(transcript)
      } finally {
        if (
          mountedRef.current &&
          generationRef.current === sessionId
        ) {
          setSubmitting(false)
        }
      }
    },
    [destroyWaveSurfer, markInactive],
  )

  const cancelActiveSession = useCallback(
    (nextError?: DictationErrorCode) => {
      generationRef.current += 1
      activeSessionIdRef.current = null
      stoppingSessionIdRef.current = null
      recordedBytesRef.current = 0
      setRecording(false)
      setListening(false)
      setSubmitting(false)
      setError(nextError ?? null)
      destroyWaveSurfer()
    },
    [destroyWaveSurfer],
  )

  const stop = useCallback(() => {
    const record = recordRef.current
    const sessionId = activeSessionIdRef.current ?? generationRef.current
    if (stoppingSessionIdRef.current === sessionId) return

    if (!record) {
      cancelActiveSession()
      return
    }

    try {
      if (record.isRecording()) {
        stoppingSessionIdRef.current = sessionId
        setListening(false)
        setSubmitting(true)
        setError(null)
        clearStopTimeout()
        stopTimeoutRef.current = setTimeout(() => {
          stopTimeoutRef.current = null
          if (
            activeSessionIdRef.current === sessionId &&
            stoppingSessionIdRef.current === sessionId
          ) {
            cancelActiveSession("audio-capture")
          }
        }, VOICE_RECORDING_STOP_TIMEOUT_MS)
        record.stopRecording()
      } else {
        cancelActiveSession()
      }
    } catch {
      cancelActiveSession("audio-capture")
    }
  }, [cancelActiveSession, clearStopTimeout])

  const abort = useCallback(() => {
    cancelActiveSession()
  }, [cancelActiveSession])

  const start = useCallback(() => {
    if (!supported) {
      setError("not-supported")
      return
    }

    if (recording || submitting) return

    const sessionId = generationRef.current + 1
    generationRef.current = sessionId
    activeSessionIdRef.current = sessionId
    stoppingSessionIdRef.current = null
    recordedBytesRef.current = 0
    setWaveformElement(null)
    setError(null)
    setSubmitting(false)
    setListening(false)
    setRecording(true)
  }, [recording, submitting, supported])

  const waveformContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) setWaveformElement(node)
  }, [])

  useEffect(() => {
    if (!recording || !waveformElement || wavesurferRef.current) return

    const sessionId = activeSessionIdRef.current
    if (sessionId == null) return

    const wavesurfer = WaveSurfer.create({
      container: waveformElement,
      height: "auto",
      waveColor: "rgba(12, 20, 18, 0.24)",
      progressColor: "rgba(12, 20, 18, 0.58)",
      cursorWidth: 0,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      barMinHeight: 1,
      interact: false,
      normalize: true,
    })
    const recordOptions: RecordPluginOptions = {
      renderRecordedAudio: false,
      scrollingWaveform: true,
      scrollingWaveformWindow: 4,
      mediaRecorderTimeslice: VOICE_RECORDING_TIMESLICE_MS,
    }
    const mimeType = preferredVoiceMimeType()
    if (mimeType) recordOptions.mimeType = mimeType

    const record = wavesurfer.registerPlugin(RecordPlugin.create(recordOptions))
    wavesurferRef.current = wavesurfer
    recordRef.current = record

    unsubscriptionsRef.current = [
      record.on("record-start", () => {
        if (activeSessionIdRef.current !== sessionId) return
        setListening(true)
      }),
      record.on("record-data-available", (blob) => {
        if (activeSessionIdRef.current !== sessionId) return
        recordedBytesRef.current += blob.size
        if (recordedBytesRef.current > VOICE_RECORDING_MAX_BYTES) {
          cancelActiveSession("too-large")
        }
      }),
      record.on("record-progress", (duration) => {
        if (activeSessionIdRef.current !== sessionId) return
        if (duration >= VOICE_RECORDING_MAX_MS) {
          stop()
        }
      }),
      record.on("record-end", (blob) => {
        void finishRecording(sessionId, blob)
      }),
    ]

    record
      .startRecording()
      .then(() => {
        if (
          mountedRef.current &&
          activeSessionIdRef.current === sessionId &&
          generationRef.current === sessionId
        ) {
          return
        }
        record.destroy()
      })
      .catch((recordingError: unknown) => {
        if (
          !mountedRef.current ||
          activeSessionIdRef.current !== sessionId ||
          generationRef.current !== sessionId
        ) {
          return
        }
        cancelActiveSession(recordingErrorCode(recordingError))
      })

    return () => {
      destroyWaveSurfer()
    }
  }, [
    cancelActiveSession,
    destroyWaveSurfer,
    finishRecording,
    recording,
    stop,
    waveformElement,
  ])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      generationRef.current += 1
      destroyWaveSurfer()
    }
  }, [destroyWaveSurfer])

  return {
    supported,
    recording: recording && !submitting,
    listening,
    transcribing: submitting,
    error,
    start,
    stop,
    abort,
    waveformContainerRef,
  }
}

function hasWaveSurferRecordingSupport(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== "undefined"
  )
}

function preferredVoiceMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined

  return [
    "audio/webm;codecs=opus",
    "audio/webm",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "audio/mp4",
    "video/mp4",
  ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
}

function voiceFileNameForMimeType(mimeType: string): string {
  switch (baseMimeType(mimeType)) {
    case "audio/flac":
      return "robin-voice.flac"
    case "audio/m4a":
    case "audio/mp4":
    case "video/mp4":
      return "robin-voice.mp4"
    case "audio/mpeg":
    case "audio/mpga":
      return "robin-voice.mp3"
    case "audio/ogg":
      return "robin-voice.ogg"
    case "audio/wav":
      return "robin-voice.wav"
    case "audio/webm":
    case "video/webm":
    default:
      return "robin-voice.webm"
  }
}

function recordingErrorCode(error: unknown): DictationErrorCode {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "SecurityError") {
      return "not-allowed"
    }
    if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
      return "audio-capture"
    }
  }

  return "audio-capture"
}

function baseMimeType(mimeType: string): string {
  return mimeType.split(";")[0]?.trim().toLowerCase() ?? ""
}
